import { useFormContext } from '../providers/FormContextProvider.js';
import { usePaymentContext } from '../providers/PaymentContextProvider.js';
import { creditCardBrandOfCardType } from '../types-internal/ModernizePaymentTypes.js';
import { useTracking } from './useTracking.js';

const useFormSubmit = () => {
    const { updateSelectedPayment, selectedPayment, checkoutState, appConfig } = usePaymentContext();
    const { getFormData, validate, adyenCreditCardFormRef, braintreeCreditCardFormRef } = useFormContext();
    const { customerId, sid, currency, customerType } = checkoutState ?? {};
    const { shop } = appConfig ?? {};
    const { siteId } = shop ?? {};
    const track = useTracking({
        sid,
        siteId,
        currency,
        customerId,
        guest: customerType === 'GUEST_CUSTOMER',
    });
    const submitHandlers = {
        K: async () => {
            const pspName = selectedPayment?.details?.paymentSetupInfo?.clientInfo?.pspName;
            let data = {
                paymentMethod: selectedPayment?.paymentMethod || null,
                paymentMethodShortName: selectedPayment?.paymentMethodShortName || null,
            };
            if (pspName === 'BRAINTREE') {
                const result = await braintreeCreditCardFormRef?.current?.tokenizeCard();
                if (result?.success) {
                    track?.('AdyenFormOK');
                    data = {
                        ...data,
                        braintree: {
                            binData: result.data.binData,
                        },
                        details: {
                            cards: [
                                {
                                    pspIdentifier: null,
                                    pspIdentifierAlternative: null,
                                    psp: 'BRAINTREE',
                                    createdAt: null,
                                    bin: result.data.details.bin,
                                    expired: false,
                                    favorite: true,
                                    nonce: result.data.nonce,
                                    brand: creditCardBrandOfCardType[result.data.details.cardType],
                                    maskedNumber: result.data.details.lastFour,
                                    holderName: result.data.details.cardholderName,
                                    expirationMonth: result.data.details.expirationMonth,
                                    expirationYear: result.data.details.expirationYear,
                                },
                            ],
                        },
                    };
                }
                else {
                    track?.('AdyenFormKO');
                }
            }
            else {
                const result = adyenCreditCardFormRef?.current?.computeAdyenCreditCardFormData();
                data = {
                    ...data,
                    storeCard: result?.storeInVault,
                    paymentMethod: selectedPayment?.paymentMethod || null,
                    paymentMethodShortName: selectedPayment?.paymentMethodShortName || null,
                    details: {
                        cards: [
                            {
                                pspIdentifier: null,
                                pspIdentifierAlternative: null,
                                psp: 'ADYEN',
                                createdAt: null,
                                bin: null,
                                expired: false,
                                favorite: true,
                                nonce: null,
                                brand: result?.creditCardBrand?.toUpperCase(),
                                maskedNumber: result?.lastFour || null,
                                expirationMonth: null,
                                expirationYear: null,
                                holderName: result?.holderName || null,
                            },
                        ],
                    },
                    adyen: {
                        encryptedCardNumber: result?.encryptedCardNumber || '',
                        encryptedExpiryMonth: result?.encryptedExpiryMonth || '',
                        encryptedExpiryYear: result?.encryptedExpiryYear || '',
                        encryptedSecurityCode: result?.encryptedSecurityCode || '',
                    },
                };
            }
            updateSelectedPayment(data);
        },
        BC: () => {
            const paymentForm = document.getElementById('payment-form');
            const isFormValid = validate('bancontact', true);
            const adyenToken = selectedPayment?.details?.paymentSetupInfo?.clientInfo?.pspToken || '';
            if (paymentForm && isFormValid) {
                const EncryptedForm = window?.adyen?.encrypt?.createEncryptedForm(paymentForm, adyenToken, {});
                const number = getFormData('bancontact', 'number').value;
                const holderName = getFormData('bancontact', 'holderName').value;
                const expiryMonth = getFormData('bancontact', 'expirationMonth').value;
                const expiryYear = getFormData('bancontact', 'expirationYear').value;
                const encryptedForm = EncryptedForm.encrypt();
                EncryptedForm.createEncryptedField(encryptedForm);
                if (encryptedForm) {
                    updateSelectedPayment({
                        bancontact: {
                            encryptedCardDetails: encryptedForm,
                        },
                        paymentMethod: selectedPayment?.paymentMethod || null,
                        paymentMethodShortName: selectedPayment?.paymentMethodShortName || null,
                        details: {
                            cards: [
                                {
                                    pspIdentifier: null,
                                    pspIdentifierAlternative: null,
                                    psp: 'ADYEN',
                                    createdAt: null,
                                    bin: null,
                                    expired: false,
                                    favorite: true,
                                    nonce: null,
                                    maskedNumber: number.substring(number.length - 4),
                                    holderName: holderName.trim(),
                                    expirationMonth: expiryMonth,
                                    expirationYear: expiryYear,
                                    brand: 'OTHER',
                                },
                            ],
                        },
                    });
                }
            }
        },
        BS: () => {
            const isFormValid = validate('sepa', true);
            if (isFormValid) {
                const holderName = getFormData('sepa', 'holderName').value;
                const number = getFormData('sepa', 'iban').value;
                updateSelectedPayment({
                    paymentMethod: selectedPayment?.paymentMethod || null,
                    paymentMethodShortName: selectedPayment?.paymentMethodShortName || null,
                    details: {
                        bankAccountDetails: {
                            owner: holderName,
                            number,
                        },
                    },
                });
            }
        },
    };
    const submitFn = (paymentMethod, defaultFn) => {
        const PM = paymentMethod;
        const paymentProviderCallBack = PM ? submitHandlers[PM] : null;
        if (paymentProviderCallBack)
            paymentProviderCallBack();
        else
            defaultFn?.();
    };
    return [submitFn];
};

export { useFormSubmit as default, useFormSubmit };
//# sourceMappingURL=useFormSubmit.js.map
